import { useFocusEffect, useRouter } from 'expo-router'
import { useCallback, useRef, useState, type ReactNode } from 'react'
import { ActivityIndicator, BackHandler, Linking, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { WebView, type WebViewNavigation } from 'react-native-webview'

import { Button } from '@/src/elements/button/default'
import { IframeError } from '@/src/elements/iframe/IframeError'
import { authTokenStorage } from '@/src/features/auth/utils/authTokenStorage'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useTranslate } from '@/src/lang/useTranslate'

import { getNativePath, isAdminUrl, isHozanaUrl } from '../utils/adminWebViewNavigation'
import { getAdminWebViewScript } from '../utils/getAdminWebViewScript'

export const AdminWebViewScreen = ({ url }: { url: string }): ReactNode => {
  const webView = useRef<WebView>(null)
  const hasLeftWebView = useRef(false)
  const [canGoBack, setCanGoBack] = useState(false)
  const [retryKey, setRetryKey] = useState(0)
  const [token] = useState(() => authTokenStorage.getToken())
  const router = useRouter()
  const { t } = useTranslate()

  const handleClose = useCallback(() => {
    if (router.canGoBack()) router.back()
    else router.replace('/')
  }, [router])

  const handleBack = useCallback(() => {
    if (canGoBack) webView.current?.goBack()
    else handleClose()
  }, [canGoBack, handleClose])

  useFocusEffect(
    useCallback(() => {
      const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
        handleBack()
        return true
      })
      return () => subscription.remove()
    }, [handleBack]),
  )

  const handleNavigation = (destination: string) => {
    const nativePath = getNativePath(destination)
    if (nativePath) {
      if (!hasLeftWebView.current) {
        hasLeftWebView.current = true
        webView.current?.stopLoading()
        router.replace(nativePath)
      }
      return false
    }

    if (!isHozanaUrl(destination)) {
      // Never load an external document into a WebView containing the user's credentials.
      if (/^(https?:|mailto:|tel:)/i.test(destination)) {
        Linking.openURL(destination)
      }
      return false
    }
    return true
  }

  const handleNavigationStateChange = (state: WebViewNavigation) => {
    setCanGoBack(state.canGoBack)
    handleNavigation(state.url)
  }

  const handleRetry = () => setRetryKey((current) => current + 1)
  const handleOpenWindow = (destination: string) => {
    if (handleNavigation(destination)) {
      webView.current?.injectJavaScript(`window.location.href = ${JSON.stringify(destination)}; true;`)
    }
  }

  return (
    <SafeAreaView className="flex-1 bg-white">
      <View className="flex-row items-center justify-between px-4 py-2">
        <Button variant="light" startIcon={ICON.CHEVRON_LEFT} onPress={handleBack} testID={TEST_ID.ADMIN.BACK}>
          {t('word.previous')}
        </Button>
        <Button variant="light" startIcon={ICON.CLOSE} onPress={handleClose} testID={TEST_ID.ADMIN.CLOSE}>
          {t('word.close')}
        </Button>
      </View>
      {isAdminUrl(url) ? (
        <WebView
          key={retryKey}
          ref={webView}
          testID={TEST_ID.ADMIN.WEBVIEW}
          style={{ flex: 1 }}
          incognito
          source={{ uri: url, ...(token ? { headers: { Cookie: `jwtToken=${encodeURIComponent(token)}` } } : {}) }}
          injectedJavaScriptBeforeContentLoaded={getAdminWebViewScript(token)}
          onShouldStartLoadWithRequest={({ url: destination, isTopFrame }) =>
            !isTopFrame || handleNavigation(destination)
          }
          onNavigationStateChange={handleNavigationStateChange}
          onMessage={({ nativeEvent }) => {
            if (isHozanaUrl(nativeEvent.url)) {
              setCanGoBack(nativeEvent.canGoBack)
              handleNavigation(nativeEvent.data)
            }
          }}
          onError={(error) => {
            console.log(error)
          }}
          onOpenWindow={({ nativeEvent }) => handleOpenWindow(nativeEvent.targetUrl)}
          startInLoadingState
          renderLoading={() => <ActivityIndicator />}
          renderError={() => <IframeError onRetry={handleRetry} />}
        />
      ) : (
        <IframeError onRetry={handleClose} />
      )}
    </SafeAreaView>
  )
}
